import React, { useState, useEffect } from 'react';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import './OurPlans.css';
import InputBoxLarge from '../../../components/common/InputBox/InputBoxLarge';
import InputAndSelectCompoBoxSmall from '../../../components/common/InputBox/InputAndSelectCompo';
import FeaturesList from '../../../components/common/FeaturesListAdd/FeaturesListAdd';
import authenticatedAxios from '../../../authenticatedAxios';
import { baseurl } from '../../../api';
import { useNavigate, useParams } from 'react-router-dom';
import ToggleSwitch from '../../../components/common/ToggleButton/ToggleSwitch';
import InputBoxSmall from '../../../components/common/InputBox/InputBoxSmall';
import LargeDetailBox from '../../../components/common/DetailBox/LargeDetailBox';
import SmallDetailBox from '../../../components/common/DetailBox/SmallDetailBox';
import FeaturesListDetail from '../../../components/common/FeaturesListAdd/FeaturesList';

// ─── Main component ──────────────────────────────────────────────────────────

const OurPlansDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [currencies, setCurrencies] = useState([]);
    const [features, setFeatures] = useState([]);
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchPlan = async () => {
        try {
            const response = await authenticatedAxios.get(`${baseurl}plans/?id=${id}`);
            if (response.data && response.data.data) {
                setPlan(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching plan details:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrencies = async () => {
        try {
            const response = await authenticatedAxios.get(`${baseurl}currency/`);
            const options = (response.data.data || []).map(currency => ({
                label: currency.symbol,
                value: currency.id
            }));
            setCurrencies(options);
        } catch (error) {
            console.error('Error fetching currencies:', error);
        }
    };

    const fetchFeatures = async () => {
        try {
            const response = await authenticatedAxios.get(`${baseurl}plans/feature/`);
            if (response.data.response_code === 1) {
                const featureOptions = response.data.data.map(feature => ({
                    label: feature.name,
                    value: feature.id,
                }));
                setFeatures(featureOptions);
            } else {
                console.error("Error fetching features:", response.data.message);
            }
        } catch (error) {
            console.error("Error fetching features:", error);
        }
    };

    useEffect(() => {
        fetchCurrencies();
        fetchFeatures();
        if (id) fetchPlan();
    }, [id]);

    if (loading) {
        return (
            <div className="whole-container">
                <p className="common-text fts-14 ftw-4">Loading...</p>
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="whole-container">
                <p className="common-text fts-14 ftw-4">Plan not found.</p>
            </div>
        );
    }

    return (
        <div className='whole-container'>
            <div className='top-heading'>
                <PageHeader pageName="Plans / Our Plans /" nestedpageName="Detail" linkTo="/our-plans" />
                <div className='export-container'>
                    <ExportLabel />
                </div>
            </div>
            <div className="page-container">
                {/* ── Plan Details Card ── */}
                <div className="card">
                    <div className="card-header">
                        <span className="card-title">Plan Details</span>
                        <button
                            className="popup-add-button common-text white fts-15 ftw-5"
                            onClick={() => navigate(`/our-plans/edit/${plan.id}`)}
                        >
                            Edit Plan
                        </button>
                    </div>

                    <div className="card-body">

                        <LargeDetailBox
                            label="Plan Name"
                            value={plan.plan_name}
                        />

                        <LargeDetailBox
                            label="Display Name"
                            value={plan.display_name}
                        />

                        <div className="row-2">
                            <SmallDetailBox
                                label="Price Monthly"
                                value={`${plan?.currency?.symbol || ''} ${plan?.monthly_price || 'N/A'}`}
                            />
                            
                            <SmallDetailBox
                                label="Price Yearly"
                                value={`${plan?.currency?.symbol || ''} ${plan?.yearly_price || 'N/A'}`}
                            />
                        </div>


                        <LargeDetailBox
                            label="Badge Text"
                            value={plan.badge_text}
                        />

                        <div className="row-2">
                            <InputBoxSmall
                                label="Default User Limit"
                                defaultValue={plan.default_users}
                                readOnly
                            />
                            <InputBoxSmall
                                label="Monthly Maps Calls"
                                defaultValue={plan.default_maps_calls_monthly}
                                readOnly
                            />
                            <InputBoxSmall
                                label="Yearly Maps Calls"
                                defaultValue={plan.default_maps_calls_yearly}
                                readOnly
                            />
                        </div>

                        <div className="row-2">
                            <InputBoxSmall
                                label="Monthly AI Credits"
                                defaultValue={plan.default_ai_credits_monthly}
                                readOnly
                            />
                            <InputBoxSmall
                                label="Yearly AI Credits"
                                defaultValue={plan.default_ai_credits_yearly}
                                readOnly
                            />
                        </div>

                        <div className="row-2">
                            <SmallDetailBox
                                label="Monthly User Price"
                                value={`${plan?.currency?.symbol || ''} ${plan?.add_user_price_monthly || 'N/A'}`}
                            />
                            <SmallDetailBox
                                label="Yearly User Price"
                                value={`${plan?.currency?.symbol || ''} ${plan?.add_user_price_yearly || 'N/A'}`}
                            />
                        </div>

                        <div className="row-2">
                            <InputBoxSmall
                                label="Maps Calls Base Limit"
                                defaultValue={plan.base_maps_calls_count}
                                readOnly
                            />
                            <SmallDetailBox
                                label="Maps Calls Base Price"
                                value={`${plan?.currency?.symbol || ''} ${plan?.base_maps_calls_price || 'N/A'}`}
                            />
                        </div>

                        <div className="row-2">
                            <InputBoxSmall
                                label="AI Credits Base Limit"
                                defaultValue={plan.base_ai_credits_count}
                                readOnly
                            />
                            <SmallDetailBox
                                label="AI Credits Base Price"
                                value={`${plan?.currency?.symbol || ''} ${plan?.base_ai_credits_price || 'N/A'}`}
                            />
                        </div>

                        <div className="toggle-group">
                            <div className="toggle-item">
                                <span className='toggle-label'>Is Free Plan</span>
                                <ToggleSwitch
                                    defaultValue={plan.is_freeplan}
                                    readOnly
                                />
                            </div>

                            <div className="toggle-item">
                                <span className='toggle-label'>Advanced Plan</span>
                                <ToggleSwitch
                                    defaultValue={plan.is_advanced}
                                    readOnly
                                />
                            </div>
                        </div>
                        {plan.is_freeplan && (
                            <LargeDetailBox
                                label="Trial Period / Days"
                                value={plan.trial_period}
                            />
                        )}
                    </div>
                </div>

                {/* ── Plan Features Card ── */}
                <div className="features-card">
                    <FeaturesListDetail
                        title="Plan Features"
                        planFeatures={plan.features?.map(f => ({
                            label: f.name,
                            id: f.id
                        }))}
                    />
                </div>
            </div>
        </div>
    );
};

export default OurPlansDetail;